import { liveQuery, type Subscription } from "dexie";
import { useEffect, useReducer } from "react";

interface SuspenseEntry<T> {
  deps: any[];
  state: {
    error: Error | null;
    result: T;
    hasResult: boolean;
  };
  promise: Promise<T>;
  subscription?: Subscription;
  subscribers: (() => void)[];
  subscribe: (next: () => void) => () => void;
  // P0-I Phase 1S-0: Suspense retry starvation instrumentation.
  // Track the suspend → resolve → commit lifecycle to detect main-thread
  // starvation on resource-constrained peers (the "whale-click" symptom).
  // All three are set at most once per entry; absence means "never happened".
  firstThrowTimestamp?: number;
  firstResolveTimestamp?: number;
  firstCommitTimestamp?: number;
}

const cache: SuspenseEntry<any>[] = [];

function shallowEqualArrays(a: any[], b: any[]) {
  if (a === b) return true;
  if (!a || !b) return false;
  const len = a.length;
  if (b.length !== len) return false;
  for (let i = 0; i < len; i++) {
    if (!Object.is(a[i], b[i])) {
      return false;
    }
  }
  return true;
}

function getOrCreateLiveQuery<T>(query: () => Promise<T> | T, deps?: any[]) {
  if (deps === undefined) {
    deps = [];
  }

  deps = [...deps];
  deps.unshift(query.toString());

  for (const entry of cache) {
    if (!shallowEqualArrays(entry.deps, deps)) {
      continue;
    }

    return entry;
  }

  let resolve: ((value: T) => void) | undefined;
  let reject: ((reason?: any) => void) | undefined;

  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  function createSubscription(entry: SuspenseEntry<T>) {
    const observable = liveQuery(query);

    return observable.subscribe(
      (val) => {
        if (
          entry.state.hasResult &&
          entry.state.result === val &&
          entry.state.error === null
        ) {
          return;
        }

        entry.state.error = null;
        entry.state.result = val;
        entry.state.hasResult = true;

        // P0-I Phase 1S-0: mark the first resolve for starvation detection.
        if (entry.firstResolveTimestamp === undefined) {
          entry.firstResolveTimestamp = performance.now();
        }

        resolve?.(val);
        resolve = undefined;
        reject = undefined;

        for (const subscriber of entry.subscribers) {
          subscriber();
        }
      },
      (err) => {
        if (entry.state.error === err) {
          return;
        }

        entry.state.error = err;

        reject?.(err);
        resolve = undefined;
        reject = undefined;

        for (const subscriber of entry.subscribers) {
          subscriber();
        }
      },
    );
  }

  const entry: SuspenseEntry<T> = {
    deps,
    state: {
      error: null,
      result: undefined as T,
      hasResult: false,
    },
    promise,
    subscribers: [],
    subscribe: (next: () => void) => {
      if (entry.subscription === undefined || entry.subscription.closed) {
        entry.subscription = createSubscription(entry);
      }

      entry.subscribers.push(next);

      return () => {
        entry.subscribers = entry.subscribers.filter((n) => n !== next);
        if (entry.subscribers.length === 0) {
          if (entry.subscription?.closed === false) {
            entry.subscription.unsubscribe();
          }
          entry.subscription = undefined;

          // P0-I Phase 1S-L1: evict the cache entry when the last
          // subscriber leaves.  Without this, every unique (query, deps)
          // combination retains its result, subscription state, and the
          // three Phase 1S-0 timestamps for the page's lifetime — a slow
          // leak amplified by per-message chat queries (three unique
          // entries per message signature: messages.get, fileMetadatas
          // lookup, sender info).  Dexie owns the source of truth, so a
          // future re-mount rebuilds a fresh subscription against
          // IndexedDB-local data with negligible cost.
          //
          // Edge case not covered: an entry whose subscription resolves
          // before any component attaches via useEffect (e.g., Suspense
          // parent unmounts during the resolve gap) will still leak its
          // entry.  That path requires very specific timing and is rare
          // enough to defer until profiling shows it.
          const idx = cache.indexOf(entry);
          if (idx >= 0) {
            cache.splice(idx, 1);
          }
        }
      };
    },
  };

  entry.subscription = createSubscription(entry);

  cache.push(entry);

  return entry;
}

export function useSuspenseLiveQuery<T>(
  query: () => Promise<T> | T,
  deps?: any[],
): T;
export function useSuspenseLiveQuery<T>(
  query: () => Promise<T> | T,
  deps: any[],
): T;
export function useSuspenseLiveQuery<T>(
  query: () => Promise<T> | T,
  deps: any[] = [],
): T {
  const [, update] = useReducer((x) => x + 1, 0);

  const entry = getOrCreateLiveQuery(query, deps);

  useEffect(() => {
    const subscription = entry.subscribe(() => update());

    // P0-I Phase 1S-0: detect Suspense retry starvation.  If we suspended
    // (firstThrowTimestamp set), the data resolved (firstResolveTimestamp
    // set), but this useEffect (which fires post-commit) lagged the resolve
    // by > 2 s, React's scheduler couldn't run the boundary retry promptly
    // — the canonical signature of main-thread saturation.  Warn once per
    // entry.  No behavior change; this is pure observability.
    if (
      entry.firstThrowTimestamp !== undefined &&
      entry.firstResolveTimestamp !== undefined &&
      entry.firstCommitTimestamp === undefined
    ) {
      entry.firstCommitTimestamp = performance.now();
      const resolveToCommitMs =
        entry.firstCommitTimestamp - entry.firstResolveTimestamp;
      if (resolveToCommitMs > 2000) {
        const throwToResolveMs =
          entry.firstResolveTimestamp - entry.firstThrowTimestamp;
        const queryKey = String(entry.deps[0] ?? "<unknown>").slice(0, 120);
        console.warn(
          `[useSuspenseLiveQuery] Suspense starvation detected: ` +
            `${resolveToCommitMs.toFixed(0)}ms from resolve→commit ` +
            `(throw→resolve: ${throwToResolveMs.toFixed(0)}ms). ` +
            `React scheduler likely starved by main-thread work. ` +
            `Query: ${queryKey}`,
        );
      }
    }

    return () => {
      subscription();
    };
  }, [entry]);

  if (!entry.state.hasResult) {
    // P0-I Phase 1S-0: mark the first throw for starvation detection.
    if (entry.firstThrowTimestamp === undefined) {
      entry.firstThrowTimestamp = performance.now();
    }
    throw entry.promise;
  }

  if (entry.state.error !== null) {
    throw entry.state.error;
  }

  return entry.state.result as T;
}
